import React, { useState, useEffect } from 'react';
import { rules } from '../data/rules';
import { benefits } from '../data/benefits';
import ProgressBar from './ProgressBar';
import StepTracker from './StepTracker';
import RuleCard from './RuleCard';
import BenefitCard from './BenefitCard';
import VideoPlayer from './VideoPlayer';
import OnboardingComplete from './OnboardingComplete';
import Navigation from './Navigation';
import { AlertTriangle } from 'lucide-react';
import { BACKEND_URL, ONBOARDING_VIDEO_URL } from '../config';

const PHASES = {
  RULES: 'rules',
  BENEFITS: 'benefits',
  VIDEO: 'video',
  COMPLETE: 'complete',
};

const OnboardingNSL = ({ validatedEmail }) => {
  const [phase, setPhase] = useState(PHASES.RULES);
  const [step, setStep] = useState(0);
  const [passedQuizzes, setPassedQuizzes] = useState({});
  const [completedBenefits, setCompletedBenefits] = useState({});
  const [showBanModal, setShowBanModal] = useState(false);
  const [acceptInput, setAcceptInput] = useState('');
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    let isInputFocused = false;

    const handleFocusIn = () => {
      isInputFocused = true;
    };

    const handleFocusOut = () => {
      if (isInputFocused) {
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          isInputFocused = false;
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const currentRule = rules[step];
  const currentBenefit = benefits[step];
  const isLastRule = step === rules.length - 1;
  const isLastBenefit = step === benefits.length - 1;

  const canProceed = () => {
    if (phase === PHASES.RULES) {
      if (isLastRule) {
        return passedQuizzes[step] && acceptInput === 'ACEPTO';
      }
      return passedQuizzes[step] || false;
    }
    if (phase === PHASES.BENEFITS) {
      return completedBenefits[step] || false;
    }
    return true;
  };

  const handleQuizPassed = () => {
    setPassedQuizzes((p) => ({ ...p, [step]: true }));
  };

  const handleBenefitComplete = () => {
    setCompletedBenefits((p) => ({ ...p, [step]: true }));
  };

  const getButtonText = () => {
    if (phase === PHASES.RULES && isLastRule) return 'ACEPTO LAS REGLAS';
    if (phase === PHASES.BENEFITS && isLastBenefit) return 'ACEPTO BENEFICIOS';
    return 'Siguiente';
  };

  const handleNext = () => {
    if (!canProceed()) return;

    if (phase === PHASES.RULES) {
      if (isLastRule) {
        setShowBanModal(true);
      } else {
        setStep((p) => p + 1);
      }
    } else if (phase === PHASES.BENEFITS) {
      if (isLastBenefit) {
        setPhase(PHASES.VIDEO);
      } else {
        setStep((p) => p + 1);
      }
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep((p) => p - 1);
    } else if (phase === PHASES.BENEFITS) {
      setPhase(PHASES.RULES);
      setStep(rules.length - 1);
    }
  };

  const handleAcceptBanWarning = () => {
    setShowBanModal(false);
    setPhase(PHASES.BENEFITS);
    setStep(0);
  };

  const handleVideoComplete = () => setPhase(PHASES.COMPLETE);

  const handleFinish = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'complete', email: validatedEmail }),
      });
      const data = await res.json();
      if (data.success) {
        window.location.href = `/join-discord?token=${data.token}`;
      } else {
        alert(data.error || 'Error al completar el onboarding');
      }
    } catch {
      alert('Error al completar el onboarding. Intenta de nuevo.');
    }
  };

  const trackerStep =
    phase === PHASES.RULES
      ? 2
      : phase === PHASES.BENEFITS
      ? 3
      : phase === PHASES.VIDEO
      ? 4
      : 5;

  const progressTotal = phase === PHASES.RULES ? rules.length : benefits.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black p-4 overflow-x-hidden relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-700"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto py-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-0">
            <img src="/logo-nsl.png" alt="NSL Logo" className="h-24 w-auto object-contain" />
          </div>
          <p className="text-gray-400 text-sm">No Soy Liquidez</p>
        </div>

        <StepTracker currentStep={trackerStep} />

        {(phase === PHASES.RULES || phase === PHASES.BENEFITS) && (
          <ProgressBar currentStep={step} totalSteps={progressTotal} />
        )}

        <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-600/20 rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden">
          <div className="p-6 sm:p-8">
            {phase === PHASES.RULES && (
              <>
                <RuleCard
                  rule={currentRule}
                  isLast={isLastRule}
                  onQuizPassed={handleQuizPassed}
                  quizPassed={passedQuizzes[step] || false}
                />
                {isLastRule && passedQuizzes[step] && (
                  <div className="mb-8">
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-yellow-600/30">
                      <p className="text-white text-center mb-4">
                        Escribe <span className="text-yellow-500 font-bold">ACEPTO</span> para confirmar que has leído todas las reglas:
                      </p>
                      <input
                        type="text"
                        value={acceptInput}
                        onChange={(e) => setAcceptInput(e.target.value.toUpperCase())}
                        placeholder="Escribe ACEPTO"
                        className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white text-center focus:outline-none focus:border-yellow-500 transition-colors"
                      />
                    </div>
                  </div>
                )}
                <Navigation
                  currentStep={step}
                  canProceed={canProceed()}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  buttonText={getButtonText()}
                  showIcon={!isLastRule}
                />
              </>
            )}

            {phase === PHASES.BENEFITS && (
              <>
                <BenefitCard
                  benefit={currentBenefit}
                  onTimerComplete={handleBenefitComplete}
                  timerComplete={completedBenefits[step] || false}
                />
                <Navigation
                  currentStep={step}
                  canProceed={canProceed()}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  buttonText={getButtonText()}
                  showIcon={!isLastBenefit}
                />
              </>
            )}

            {phase === PHASES.VIDEO && (
              <VideoPlayer videoUrl={ONBOARDING_VIDEO_URL} onVideoComplete={handleVideoComplete} />
            )}

            {phase === PHASES.COMPLETE && (
              <OnboardingComplete onFinish={handleFinish} validatedEmail={validatedEmail} />
            )}
          </div>
        </div>

        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>© {currentYear} No Soy Liquidez. Todos los derechos reservados.</p>
        </div>
      </div>

      {showBanModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/50 rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                ⚠️ ADVERTENCIA ⚠️
              </h3>
              <p className="text-red-400 text-lg font-semibold mb-6">
                RECUERDA QUE SI VIOLAS UNA DE ESTAS REGLAS SERÁS BANEADO DE POR VIDA EN LA COMUNIDAD.
              </p>
              <button
                onClick={handleAcceptBanWarning}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-yellow-500/50"
              >
                ENTENDIDO, ACEPTO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingNSL;